import React, { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { setError, setHomepageSettings, setLoading } from '../store/slices/homepageSlice'
import { fetchHomepageSettingsFromSupabase } from '../services/publicSupabase'

const canonicalizeUnitType = (value: string): string | null => {
  const normalized = String(value || '').trim().toLowerCase()

  if (!normalized) {
    return null
  }

  if (normalized.includes('bigger')) {
    return 'Bigger 6-Sleeper Unit'
  }

  if (normalized.includes('4')) {
    return '4-Sleeper Unit'
  }

  if (normalized.includes('6')) {
    return '6-Sleeper Unit'
  }

  return null
}

const getAccommodationUnitsHref = (unitType: string) => {
  const canonicalType = canonicalizeUnitType(unitType) || unitType

  return `/booking-system/rooms?type=${encodeURIComponent(canonicalType)}`
}

const HomePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { settings, loading, error } = useAppSelector((state) => state.homepage)

  useEffect(() => {
    let isMounted = true

    const loadHomepageSettings = async () => {
      dispatch(setLoading(true))

      try {
        const settings = await fetchHomepageSettingsFromSupabase()

        if (!isMounted) {
          return
        }

        dispatch(setHomepageSettings(settings))
      } catch {
        if (!isMounted) {
          return
        }

        dispatch(setError('Unable to load homepage settings.'))
      }
    }

    void loadHomepageSettings()

    return () => {
      isMounted = false
    }
  }, [dispatch])

  const pageStyle = {
    minHeight: '100vh',
    position: 'relative' as const,
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: 'transparent',
    marginTop: '-80px',
    paddingTop: '80px'
  }

  const overlayStyle = {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.4), rgba(15, 23, 42, 0.5), rgba(8, 145, 178, 0.6))'
  }

  const containerStyle = {
    position: 'relative' as const,
    maxWidth: '1280px',
    margin: '0 auto',
    padding: '80px 16px',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center'
  }

  const cardStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '32px',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(8, 145, 178, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#0f172a',
    maxWidth: '600px'
  }

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    marginBottom: '24px',
    lineHeight: '1.2',
    color: '#0f172a'
  }

  const subtitleStyle = {
    fontSize: '1.25rem',
    color: '#f97316',
    fontWeight: '500',
    marginBottom: '16px'
  }

  const descriptionStyle = {
    fontSize: '1.125rem',
    marginBottom: '32px',
    color: '#475569',
    lineHeight: '1.625'
  }

  const buttonContainerStyle = {
    display: 'flex',
    gap: '16px',
    flexDirection: 'column' as const
  }

  const primaryButtonStyle = {
    backgroundColor: '#0891b2',
    color: 'white',
    padding: '16px 32px',
    fontSize: '1.125rem',
    fontWeight: '600',
    textDecoration: 'none',
    borderRadius: '12px',
    textAlign: 'center' as const,
    border: '2px solid #0891b2',
    transition: 'all 0.3s ease',
    display: 'block'
  }

  const secondaryButtonStyle = {
    backgroundColor: 'white',
    color: '#0891b2',
    padding: '16px 32px',
    fontSize: '1.125rem',
    fontWeight: '600',
    textDecoration: 'none',
    borderRadius: '12px',
    textAlign: 'center' as const,
    border: '2px solid #0891b2',
    transition: 'all 0.3s ease',
    display: 'block'
  }

  const sectionPanelStyle = {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding: '48px',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(8, 145, 178, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.2)'
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={overlayStyle}></div>
        <div style={containerStyle}>
          <div style={cardStyle}>
            <div style={{ height: '56px', width: '80%', borderRadius: '12px', backgroundColor: 'rgba(148, 163, 184, 0.18)' }}></div>
            <div style={{ marginTop: '20px', height: '24px', width: '50%', borderRadius: '10px', backgroundColor: 'rgba(148, 163, 184, 0.16)' }}></div>
            <div style={{ marginTop: '18px', height: '88px', width: '100%', borderRadius: '12px', backgroundColor: 'rgba(148, 163, 184, 0.14)' }}></div>
            <div style={{ marginTop: '32px', display: 'grid', gap: '16px' }}>
              <div style={{ height: '56px', borderRadius: '12px', backgroundColor: 'rgba(8, 145, 178, 0.16)' }}></div>
              <div style={{ height: '56px', borderRadius: '12px', backgroundColor: 'rgba(15, 23, 42, 0.08)' }}></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={overlayStyle}></div>
        <div style={containerStyle}>
          <div style={cardStyle}>
            <h1 style={titleStyle}>Booking system temporarily unavailable</h1>
            <p style={descriptionStyle}>
              We could not load the latest Marina Glen booking details right now. Please refresh the page in a moment or contact the resort directly if the issue continues.
            </p>
            <div style={buttonContainerStyle}>
              <a
                href="tel:+27393121234"
                style={primaryButtonStyle}
              >
                Call Marina Glen
              </a>
              <a
                href="mailto:bookings@marinaglen.co.za"
                style={secondaryButtonStyle}
              >
                Email Bookings
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <div style={overlayStyle}></div>
      <div style={containerStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>
            {settings.heroTitle}
          </h1>
          <p style={subtitleStyle}>
            {settings.heroSubtitle}
          </p>
          <p style={descriptionStyle}>
            {settings.heroDescription}
          </p>
          <div style={buttonContainerStyle}>
            <a
              href="/booking-system/rooms"
              style={primaryButtonStyle}
            >
              Explore Our Units
            </a>
            <a
              href="/booking-system/booking"
              style={secondaryButtonStyle}
            >
              Book Your Stay
            </a>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div style={{ 
        padding: '80px 16px', 
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)'
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', textAlign: 'center' as const }}>
          <div style={sectionPanelStyle}>
            <h2 style={{ 
              fontSize: '2.5rem', 
              fontWeight: 'bold', 
              color: '#0f172a', 
              marginBottom: '16px' 
            }}>
              {settings.featuresTitle}
            </h2>
            <p style={{ 
              fontSize: '1.25rem', 
              color: '#475569', 
              marginBottom: '48px',
              maxWidth: '600px',
              margin: '0 auto 48px auto'
            }}>
              {settings.featuresSubtitle}
            </p>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '32px' 
            }}>
              {settings.featureCards.map((card) => (
                <div key={card.id} style={{
                  textAlign: 'center' as const,
                  padding: '32px',
                  borderRadius: '12px',
                  border: '1px solid rgba(8, 145, 178, 0.2)',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    backgroundColor: 'rgba(8, 145, 178, 0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px auto',
                    fontSize: '2rem'
                  }}>
                    {card.emoji}
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '16px' }}>
                    {card.title}
                  </h3>
                  <p style={{ color: '#475569' }}>
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '80px 16px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={sectionPanelStyle}>
            <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '16px', textAlign: 'center' as const }}>
              {settings.accommodationTitle}
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#475569', margin: '0 auto 48px auto', maxWidth: '700px', textAlign: 'center' as const }}>
              {settings.accommodationSubtitle}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
              {settings.unitDescriptions
                .filter((unit) => unit.unitType !== 'Bigger 6 sleeper Unit')
                .map((unit) => (
                <div key={unit.unitType} style={{
                  padding: '28px',
                  borderRadius: '14px',
                  border: '1px solid rgba(8, 145, 178, 0.2)',
                  backgroundColor: 'rgba(255, 255, 255, 0.75)'
                }}>
                  <h3 style={{ fontSize: '1.35rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px' }}>
                    {unit.title}
                  </h3>
                  <p style={{ color: '#475569', marginBottom: '20px', lineHeight: '1.6' }}>
                    {unit.description}
                  </p>
                  <a
                    href={getAccommodationUnitsHref(unit.unitType)}
                    style={{ ...primaryButtonStyle, padding: '12px 20px', fontSize: '1rem' }}
                  >
                    View Unit
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '0 16px 80px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{
            ...sectionPanelStyle,
            textAlign: 'center' as const,
            background: 'linear-gradient(135deg, rgba(8, 145, 178, 0.92), rgba(14, 116, 144, 0.92))',
            color: '#ffffff'
          }}>
            <h2 style={{ fontSize: '2.4rem', fontWeight: 'bold', marginBottom: '16px', color: '#ffffff' }}>
              {settings.ctaTitle}
            </h2>
            <p style={{ fontSize: '1.15rem', margin: '0 auto 28px auto', maxWidth: '700px', color: 'rgba(255,255,255,0.88)' }}>
              {settings.ctaDescription}
            </p>
            <div style={{ display: 'inline-flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' as const }}>
              <a href="/booking-system/rooms" style={{ ...secondaryButtonStyle }}>
                Browse Units
              </a>
              <a href="/booking-system/booking" style={{ ...primaryButtonStyle, backgroundColor: '#0f172a', border: '2px solid #0f172a' }}>
                Start Booking
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomePage